/**
 * Personel girişi — POST /api/v1/kimlik/personel/giris (GV-04).
 *
 * Ekran aynı zamanda KK-801'in ikinci yarısıdır: müdür birden fazla okuldan
 * sorumluysa her okul için BİR KEZ giriş yapar, panel oturumları yan yana
 * tutar ve üst bardan geçiş yapılır. Bu yüzden ekran hem "ilk giriş" hem
 * "okul oturumu ekle" görevini üstlenir.
 */
import type { FormEvent } from "react";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Alan, Buton, Rozet, Uyari } from "@kantin/ui";
import { YonetimMarkasi } from "./YonetimMarkasi.js";
import { girisYap, zorunluParolaDegistir } from "./veri/api.js";
import { oturumDurumu } from "./veri/oturum.js";
import { hataMetni } from "./ortak.js";

export function Giris({ vazgec }: { vazgec?: () => void }) {
  const istemci = useQueryClient();
  const oturumEkle = oturumDurumu((s) => s.oturumEkle);
  const mevcutOturumlar = oturumDurumu((s) => s.oturumlar);

  const [okulKodu, setOkulKodu] = useState("");
  const [eposta, setEposta] = useState("");
  const [parola, setParola] = useState("");
  const [yeniParola, setYeniParola] = useState("");
  const [yeniParolaTekrar, setYeniParolaTekrar] = useState("");
  const [degisimTokeni, setDegisimTokeni] = useState<string | null>(null);
  const [hata, setHata] = useState<unknown>(null);
  const [gonderiliyor, setGonderiliyor] = useState(false);

  const gonder = async (olay: FormEvent) => {
    olay.preventDefault();
    setHata(null);
    setGonderiliyor(true);
    try {
      if (degisimTokeni && yeniParola !== yeniParolaTekrar) {
        throw new Error("Yeni parolalar eşleşmiyor.");
      }
      const yanit = degisimTokeni
        ? await zorunluParolaDegistir(degisimTokeni, yeniParola)
        : await girisYap(okulKodu.trim(), eposta.trim(), parola);
      if ("parola_degisim_zorunlu" in yanit && yanit.parola_degisim_zorunlu) {
        setDegisimTokeni(yanit.degisim_token);
        setParola("");
        return;
      }
      // Önceki okulun önbelleği yeni okulun ekranında bir an bile görünmemeli.
      istemci.clear();
      oturumEkle({
        okulId: yanit.okul.id,
        okulAd: yanit.okul.ad,
        okulKodu: okulKodu.trim(),
        token: yanit.token,
        personel: yanit.personel,
      });
    } catch (e) {
      setHata(e);
    } finally {
      setGonderiliyor(false);
    }
  };

  return (
    <main className="yonetim-giris">
      <aside className="yonetim-giris-hikaye">
        <YonetimMarkasi />
        <div className="yonetim-giris-mesaji">
          <h2>
            Okul kantininiz,
            <br />
            tek bir yerden.
          </h2>
          <p>
            Satışları izleyin, stokları yönetin, hesapları güvenle takip edin.
            Okulunuzun günlük işleyişi bir bakışta önünüzde.
          </p>
        </div>
        <div className="yonetim-giris-ozellikler">
          <div>
            <strong>Satış ve stok</strong>
            <span>Günlük işleyiş</span>
          </div>
          <div>
            <strong>Kişi ve kart</strong>
            <span>Tek merkezden yönetim</span>
          </div>
          <div>
            <strong>Raporlar</strong>
            <span>Gelir ve gider takibi</span>
          </div>
        </div>
      </aside>
      <div className="yonetim-giris-form-alani">
        <div className="yonetim-giris-form">
          <header className="mb-8">
            <h1 className="m-0 text-2xl">
              {vazgec ? "Okul oturumu ekle" : "Hoş geldiniz"}
            </h1>
            <p className="m-0 mt-3 text-murekkep-2">
              Okul yönetim paneline personel hesabınızla giriş yapın.
            </p>
          </header>
          <form
            className="flex flex-col gap-4"
            onSubmit={(o) => void gonder(o)}
          >
            {degisimTokeni ? (
              <>
                <Uyari tur="uyari" baslik="Parola değişimi zorunlu">
                  Geçici parola ile normal oturum açılmaz. 8–128 karakterlik
                  kalıcı parolanızı belirleyin.
                </Uyari>
                <Alan
                  etiket="Yeni parola"
                  type="password"
                  minLength={8}
                  maxLength={128}
                  required
                  value={yeniParola}
                  onChange={(e) => setYeniParola(e.target.value)}
                />
                <Alan
                  etiket="Yeni parola tekrar"
                  type="password"
                  minLength={8}
                  maxLength={128}
                  required
                  value={yeniParolaTekrar}
                  onChange={(e) => setYeniParolaTekrar(e.target.value)}
                />
              </>
            ) : (
              <>
                <Alan
                  etiket="Okul kodu"
                  name="okul_kodu"
                  autoComplete="organization"
                  required
                  value={okulKodu}
                  onChange={(e) => setOkulKodu(e.target.value)}
                  ipucu="Okulunuza verilen kısa kod"
                />
                <Alan
                  etiket="E-posta"
                  name="eposta"
                  type="email"
                  autoComplete="username"
                  required
                  value={eposta}
                  onChange={(e) => setEposta(e.target.value)}
                />
                <Alan
                  etiket="Parola"
                  name="parola"
                  type="password"
                  autoComplete="current-password"
                  required
                  value={parola}
                  onChange={(e) => setParola(e.target.value)}
                />
              </>
            )}

            {/* Hata canlı bölgede: ekran okuyucu da duymalıdır. */}
            {hata != null && (
              <Uyari tur="hata" baslik="Giriş yapılamadı">
                {hataMetni(hata)}
              </Uyari>
            )}

            <div className="flex flex-wrap gap-2">
              <Buton
                type="submit"
                tur="birincil"
                tamGenislik={!vazgec}
                yukleniyor={gonderiliyor}
              >
                {degisimTokeni ? "Parolayı değiştir ve giriş yap" : "Giriş yap"}
              </Buton>
              {vazgec && (
                <Buton tur="ikincil" onClick={vazgec} disabled={gonderiliyor}>
                  Vazgeç
                </Buton>
              )}
            </div>
          </form>

          {mevcutOturumlar.length > 0 && (
            <div className="mt-3 rounded-lg border border-cetvel bg-yuzey p-3">
              <p className="m-0 text-xs text-murekkep-3">
                Açık okul oturumları
              </p>
              <ul className="m-0 mt-2 flex flex-wrap gap-2 p-0">
                {mevcutOturumlar.map((o) => (
                  <li key={o.okulId} className="list-none">
                    <Rozet tur="notr">{o.okulAd}</Rozet>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="yonetim-giris-dipnot">
            Kantin Yönetim · Okulunuza özel çalışma alanı
          </p>
        </div>
      </div>
    </main>
  );
}
